import { z } from 'zod'

import { apiClient } from '../client'
import { canopyPath } from '../routes'
import { putBytesToSignedUrl, type PutBytesOptions } from '../signed-upload'

export type { PutBytesOptions }

export const documentSchema = z.looseObject({
  id: z.string(),
  companyId: z.string(),

  companyName: z.string().nullish(),

  fileName: z.string(),
  documentType: z.string().nullish(),
  mimeType: z.string().nullish(),
  periodCovered: z.string().nullish(),
  parseStatus: z.string().nullish(),

  sizeBytes: z.number().nullish(),
  uploadedAt: z.string().nullish(),

  parseError: z.string().nullish(),
})

export type CanopyDocument = z.infer<typeof documentSchema>

export const documentPageSchema = z.looseObject({
  items: z.array(documentSchema),
  page: z.number().int(),
  pageSize: z.number().int(),
  total: z.number().int(),
})

export type DocumentPage = z.infer<typeof documentPageSchema>

export interface ListDocumentsOptions {
  companyId?: string | undefined
  page?: number | undefined

  pageSize?: number | undefined
  signal?: AbortSignal | undefined
}

export function listDocuments(options: ListDocumentsOptions = {}): Promise<DocumentPage> {
  const { companyId, page, pageSize, signal } = options

  return apiClient().get(canopyPath('/documents'), {
    schema: documentPageSchema,
    query: { companyId, page, pageSize },
    ...(signal ? { signal } : {}),
  })
}

export function getDocument(documentId: string, signal?: AbortSignal): Promise<CanopyDocument> {
  return apiClient().get(canopyPath(`/documents/${encodeURIComponent(documentId)}`), {
    schema: documentSchema,
    ...(signal ? { signal } : {}),
  })
}

export const downloadUrlSchema = z.looseObject({
  url: z.string(),
  expiresInSeconds: z.number(),
})

export type DownloadUrl = z.infer<typeof downloadUrlSchema>

export function getDocumentDownloadUrl(
  documentId: string,
  signal?: AbortSignal,
): Promise<DownloadUrl> {
  return apiClient().get(canopyPath(`/documents/${encodeURIComponent(documentId)}/download-url`), {
    schema: downloadUrlSchema,
    ...(signal ? { signal } : {}),
  })
}

export const integrityCheckSchema = z.looseObject({
  scanned: z.number().int(),

  ok: z.number().int(),
  missing: z.array(
    z.looseObject({
      documentId: z.string(),
      companyId: z.string(),
      fileName: z.string(),
    }),
  ),
})

export type IntegrityCheck = z.infer<typeof integrityCheckSchema>

export function checkDocumentIntegrity(signal?: AbortSignal): Promise<IntegrityCheck> {
  return apiClient().get(canopyPath('/documents/integrity-check'), {
    schema: integrityCheckSchema,
    ...(signal ? { signal } : {}),
  })
}

export const DOCUMENT_TYPES = [
  'board_deck',
  'management_update',
  'growth_memo',
  'diligence',
  'analyst_report',
  'win_loss',
  'other',
] as const

export type DocumentType = (typeof DOCUMENT_TYPES)[number]

export const MAX_UPLOAD_BYTES = 104_857_600

export const uploadReservationSchema = z.looseObject({
  documentId: z.string(),
  uploadUrl: z.string(),
  s3Key: z.string().nullish(),
  expiresInSeconds: z.number().nullish(),

  requiredHeaders: z.record(z.string(), z.union([z.string(), z.number()])).nullish(),
})

export type UploadReservation = z.infer<typeof uploadReservationSchema>

export interface CreateUploadUrlInput {
  companyId: string
  fileName: string
  contentType: string
  sizeBytes: number
  documentType?: DocumentType | undefined
  periodCovered?: string | undefined
}

export function createDocumentUploadUrl(
  input: CreateUploadUrlInput,
  signal?: AbortSignal,
): Promise<UploadReservation> {
  return apiClient().post(canopyPath('/documents/upload-url'), {
    schema: uploadReservationSchema,
    body: input,
    ...(signal ? { signal } : {}),
  })
}

export function putDocumentBytes(
  reservation: UploadReservation,
  file: Blob,
  options: PutBytesOptions = {},
): Promise<void> {
  return putBytesToSignedUrl(reservation, file, options)
}

export function completeDocumentUpload(
  documentId: string,
  signal?: AbortSignal,
): Promise<CanopyDocument> {
  return apiClient().post(canopyPath(`/documents/${encodeURIComponent(documentId)}/complete`), {
    schema: documentSchema,
    ...(signal ? { signal } : {}),
  })
}

export const deleteDocumentResultSchema = z.looseObject({ success: z.boolean() })

export type DeleteDocumentResult = z.infer<typeof deleteDocumentResultSchema>

export function deleteDocument(
  documentId: string,
  signal?: AbortSignal,
): Promise<DeleteDocumentResult> {
  return apiClient().delete(canopyPath(`/documents/${encodeURIComponent(documentId)}`), {
    schema: deleteDocumentResultSchema,
    ...(signal ? { signal } : {}),
  })
}
